// "Training and Validation Data": the dataset facts for a model, laid out as one
// table with a row per fact and a column per dataset. It used to be two side-by-side
// blocks repeating every label; the table form halves the height and puts the two
// datasets next to each other, which is the comparison people actually make.
import React from 'react';
import { inject, observer } from 'mobx-react';
import numeral from 'numeral';
import moment from 'moment';
import Store from '../../../store';
import {
  Card,
  Button,
  Modal,
  ButtonToolbar,
  OverlayTrigger,
  Popover,
  Table,
  Tooltip,
} from 'react-bootstrap';
import checkPermissions from '../CheckPermissions';
import { ResponsiveLayoutContext } from '../layout/responsive-layout-context';
import { PROFILES_PERMISSIONS } from '../../../permissions';
import { LinkContainer } from 'react-router-bootstrap';
import AdminSetupLink from '../generic_components/AdminSetupLink';
import Spinner from '../Spinner';
import { LtbDatasetVariables } from '../../../models/audience/ltb/LtbDataset';
import {
  IS_LLTB,
  MAX_NUMBER_OF_LOADING_DATASETS,
} from '../../../utils/constants';
import { DataSetQuality } from '../../../models/data/types';
import DataSetOverviews from '../../../models/data/DataSetOverviews';
import { TimelineBands } from './TimelineBands';
import { buildTimeline } from './datasetTimeline';
import { DatasetModal } from './DatasetModal';
import './datasetSummary.css';

interface LoadedDatasetOverviewProps {
  store?: Store;
  tenant: string;
  overviews: DataSetOverviews;
  modelId: string;
  cleanCache: () => void;
  cleaningCache: boolean;
}

type LoadedDatasetOverviewState = {
  isLoading: boolean;
  showRefreshDatasetModal: boolean;
  /** The datasets dialog, which replaces navigating to `/import_data`. */
  showDatasetModal: boolean;
  currentLoadingModels: number[];
};

// One fact, both datasets. `hint` rides next to the label so an explanation that
// covers the whole row is stated once instead of once per column.
type DatasetRow = {
  label: string;
  hint?: React.ReactNode;
  training: React.ReactNode;
  validation: React.ReactNode;
};

// Audiences and conversion types are defined in the admin app, not in Studio, so the
// value links out to the screen that owns it. The tenant doubles as the admin org id,
// the same way the sidebar's "Admin Center" link builds its URL.
//
// Which page that is depends on a flag the admin app owns, so the path is not written
// here: `AdminSetupLink` resolves it, the same as the sidebar. This file used to hold the
// newer pair, `/audiences` and `/conversions`, which redirect to the org home when the
// flag is off.

function MappingLink({
  tenant,
  page,
  value,
}: {
  tenant: string;
  page: 'audience' | 'conversion';
  value?: React.ReactNode;
}) {
  if (!value) return null;
  return (
    <AdminSetupLink tenant={tenant} page={page}>
      {value}
    </AdminSetupLink>
  );
}

const dataQualityVariants: Record<DataSetQuality, string> = {
  average: 'warning',
  bad: 'danger',
  good: 'success',
};

// "Feb 2024 - Aug 2024" over "6 months". Returns null when either bound is missing,
// so a build type that has no date range simply drops the row.
function Timeframe({ start, end }: { start?: string; end?: string }) {
  if (!start || !end) return null;
  const from = moment(start);
  const to = moment(end);
  if (!from.isValid() || !to.isValid()) return null;
  const months = Math.max(1, Math.round(to.diff(from, 'months', true)));
  return (
    <>
      <div>
        {from.format('MMM YYYY')} - {to.format('MMM YYYY')}
      </div>
      <div className="text-muted small">
        {months} month{months === 1 ? '' : 's'}
      </div>
    </>
  );
}

// A record count carries a quality judgement when the dataset is small, so the
// number and its warning travel together.
function RecordCount({
  value,
  quality,
  id,
}: {
  value: React.ReactNode;
  quality: DataSetQuality;
  id: string;
}) {
  if (quality === 'good') return <>{value}</>;
  return (
    <OverlayTrigger
      rootClose={true}
      placement="left"
      overlay={
        <Popover id={`popover-${id}`}>
          <Popover.Body>
            <span className={`text-${dataQualityVariants[quality]}`}>
              {quality === 'bad'
                ? 'Dataset is too small!'
                : 'Dataset is of medium size.'}
            </span>
          </Popover.Body>
        </Popover>
      }
    >
      <span className="d-inline-flex align-items-center gap-1">
        {value}
        <i
          aria-hidden
          className={`fas fa-exclamation-circle ms-1 text-${dataQualityVariants[quality]}`}
        />
      </span>
    </OverlayTrigger>
  );
}

const conversionRateHint = (
  <OverlayTrigger
    rootClose={true}
    placement="top"
    overlay={
      <Popover id="popover">
        <Popover.Body>
          <b>
            Why is the conversion rate of the training dataset much higher than
            of the validation dataset?
          </b>{' '}
          The training dataset is built to have a minimum conversion rate by
          rebalancing the number of non-converters compared to the number of
          converters to reduce the risk associated to class imbalance.{' '}
          <a
            href="https://help.madkudu.com/docs/why-is-the-training-dataset-conversion-rate-higher-than-you-expect"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn more.
          </a>
        </Popover.Body>
      </Popover>
    }
    trigger="click"
  >
    <i
      aria-hidden
      className="fas fa-question-circle ms-1 text-info"
      role="button"
    />
  </OverlayTrigger>
);

export default inject('store')(
  observer(
    class LoadedDatasetOverview extends React.Component<
      LoadedDatasetOverviewProps,
      LoadedDatasetOverviewState
    > {
      constructor(props: LoadedDatasetOverviewProps) {
        super(props);

        this.state = {
          isLoading: true,
          showRefreshDatasetModal: false,
          showDatasetModal: false,
          currentLoadingModels: [],
        };

        this.toggleShowRefreshDatasetModal =
          this.toggleShowRefreshDatasetModal.bind(this);
        this.toggleShowDatasetModal = this.toggleShowDatasetModal.bind(this);
        this.reloadDataset = this.reloadDataset.bind(this);
      }

      async componentDidMount() {
        const { tenant, activeModel } = this.props.store;
        const [currentLoadingModels] = await Promise.all([
          this.props.store.mkAudiencePage.getCurrentLoadingModels(tenant),
          activeModel.type === 'customer_fit'
            ? this.props.store.getDefaultDatasetVariables(activeModel.buildType)
            : this.props.store.getDefaultDatasetLtbVariables(),
          this.props.store.getTenantAudiences(),
          this.props.store.getTenantConversionNames(),
        ]);

        this.setState({ isLoading: false, currentLoadingModels });
      }

      toggleShowRefreshDatasetModal() {
        this.setState({
          showRefreshDatasetModal: !this.state.showRefreshDatasetModal,
        });
      }

      toggleShowDatasetModal() {
        this.setState({ showDatasetModal: !this.state.showDatasetModal });
      }

      async reloadDataset() {
        await this.props.store.recompute();
      }

      // The rows are assembled rather than hand-written per dataset so a field can
      // never render for training and silently go missing for validation.
      buildRows(): DatasetRow[] {
        const { overviews } = this.props;
        const {
          activeModel,
          csvPage,
          redshiftPage,
          mkAudiencePage,
          pqlPage,
          mqaPage,
        } = this.props.store;

        const training = overviews.get('training');
        const validation = overviews.get('validation');
        const isMqa = activeModel.type === 'mqa';

        let ltbVariables: LtbDatasetVariables;
        if (IS_LLTB(activeModel.type)) {
          ltbVariables = pqlPage.variables;
        } else if (isMqa) {
          ltbVariables = mqaPage.variables;
        }

        const { tenant } = this.props;
        const rows: DatasetRow[] = [];

        if (activeModel.buildType === 'mk_audience') {
          const { trainingDataset, validationDataset } =
            mkAudiencePage.variables;
          const timeframe = (
            <Timeframe
              start={trainingDataset.startDate}
              end={trainingDataset.endDate}
            />
          );
          const validationTimeframe = (
            <Timeframe
              start={validationDataset.startDate}
              end={validationDataset.endDate}
            />
          );
          if (trainingDataset.startDate || validationDataset.startDate) {
            rows.push({
              label: 'Timeframe',
              training: timeframe,
              validation: validationTimeframe,
            });
          }
          if (trainingDataset.audience || validationDataset.audience) {
            rows.push({
              label: 'Audience',
              training: (
                <MappingLink
                  tenant={tenant}
                  page="audience"
                  value={trainingDataset.audience}
                />
              ),
              validation: (
                <MappingLink
                  tenant={tenant}
                  page="audience"
                  value={validationDataset.audience}
                />
              ),
            });
          }
          rows.push({
            label: 'Conversion type',
            training: (
              <MappingLink
                tenant={tenant}
                page="conversion"
                value={trainingDataset.conversionModel}
              />
            ),
            validation: (
              <MappingLink
                tenant={tenant}
                page="conversion"
                value={validationDataset.conversionModel}
              />
            ),
          });
        }

        if (activeModel.buildType === 'ltb') {
          if (ltbVariables.trainingDates || ltbVariables.evaluationDate) {
            rows.push({
              label: 'Timeframe',
              training: ltbVariables.trainingDates,
              validation: ltbVariables.evaluationDate,
            });
          }
          if (
            ltbVariables.trainingAudience ||
            ltbVariables.validationAudience
          ) {
            rows.push({
              label: 'Audience',
              training: (
                <MappingLink
                  tenant={tenant}
                  page="audience"
                  value={ltbVariables.trainingAudience}
                />
              ),
              validation: (
                <MappingLink
                  tenant={tenant}
                  page="audience"
                  value={ltbVariables.validationAudience}
                />
              ),
            });
          }
          rows.push({
            label: 'Conversion type',
            training: (
              <MappingLink
                tenant={tenant}
                page="conversion"
                value={ltbVariables.trainingConversionModel}
              />
            ),
            validation: (
              <MappingLink
                tenant={tenant}
                page="conversion"
                value={ltbVariables.validationConversionModel}
              />
            ),
          });
        }

        if (activeModel.buildType === 'csv_file') {
          rows.push({
            label: 'File name',
            training: csvPage.variables.trainingFileName,
            validation: csvPage.variables.validationFileName,
          });
        }

        if (activeModel.buildType === 'from_redshift_tables') {
          rows.push({
            label: 'From Redshift table',
            training: redshiftPage.variables.trainingTableName,
            validation: redshiftPage.variables.validationTableName,
          });
        }

        const count = (n: React.ReactNode) =>
          n === null || n === undefined ? n : numeral(n).format('0,0');

        rows.push({
          label: isMqa ? 'Accounts' : 'Records',
          training: (
            <RecordCount
              id="training"
              quality={training.dataSetQuality}
              value={count(isMqa ? training.domainRecords : training.records)}
            />
          ),
          validation: (
            <RecordCount
              id="validation"
              quality={validation.dataSetQuality}
              value={count(
                isMqa ? validation.domainRecords : validation.records
              )}
            />
          ),
        });
        rows.push({
          label: isMqa ? 'Converted accounts' : 'Conversions',
          training: count(
            isMqa ? training.domainConversions : training.conversions
          ),
          validation: count(
            isMqa ? validation.domainConversions : validation.conversions
          ),
        });
        rows.push({
          label: 'Conversion rate',
          hint: conversionRateHint,
          training: `${numeral(
            isMqa ? training.domainRate : training.rate
          ).format('0.0')}%`,
          validation: `${numeral(
            isMqa ? validation.domainRate : validation.rate
          ).format('0.0')}%`,
        });

        return rows;
      }

      /**
       * The card's contents when the two date windows can be drawn: the setup stated
       * once, the windows as bands, and the counts beneath them.
       *
       * Null for every build type that has no start and end date to place (LTB's
       * `trainingDates` is one string, CSV has filenames, Redshift has table names), and
       * null for any pair of dates `buildTimeline` refuses. The caller falls back to the
       * table, which still handles all of those.
       */
      renderSummary(): React.ReactNode {
        const { overviews, tenant } = this.props;
        const { activeModel, mkAudiencePage, csvPage, redshiftPage } =
          this.props.store;

        // Three build types, one block, and the bands are the part that is optional.
        //
        // An uploaded CSV has no dates at all. `trainingFileName` is whatever the person
        // called the file (`back/datasets/customer_fit/auto.ts` stores the upload's own
        // name), so the range inside `..._2024-09-01_2025-08-31.csv` is somebody's naming
        // convention and not a field, and reading a timeline out of it would be inventing
        // data from the shape of a string. What that model HAS is two file names, so that
        // is what it shows; the counts underneath are the same in every case.
        //
        // LTB keeps the table. Its dates are one free-form string per side rather than a
        // start and an end, so there is nothing to place on an axis or to line up.
        //
        // The third element is the VALIDATION value, and it is null whenever the two
        // datasets agree. Audience and conversion type describe the model rather than the
        // split, so on almost every model they are one value, and a column repeating its
        // neighbour is what made this a table in the first place.
        const facts: [string, React.ReactNode, React.ReactNode][] = [];
        let timeline = null;

        if (activeModel.buildType === 'mk_audience') {
          const { trainingDataset, validationDataset } =
            mkAudiencePage.variables;
          timeline = buildTimeline(
            { start: trainingDataset.startDate, end: trainingDataset.endDate },
            {
              start: validationDataset.startDate,
              end: validationDataset.endDate,
            }
          );
          if (!timeline) return null;
          const audience = (value?: string) => (
            <MappingLink tenant={tenant} page="audience" value={value} />
          );
          const conversion = (value?: string) => (
            <MappingLink tenant={tenant} page="conversion" value={value} />
          );
          facts.push([
            'Audience',
            audience(trainingDataset.audience),
            trainingDataset.audience === validationDataset.audience
              ? null
              : audience(validationDataset.audience),
          ]);
          facts.push([
            'Converts on',
            conversion(trainingDataset.conversionModel),
            trainingDataset.conversionModel ===
            validationDataset.conversionModel
              ? null
              : conversion(validationDataset.conversionModel),
          ]);
        } else if (activeModel.buildType === 'csv_file') {
          const { trainingFileName, validationFileName } = csvPage.variables;
          if (!trainingFileName && !validationFileName) return null;
          facts.push(['Training file', trainingFileName, null]);
          facts.push(['Validation file', validationFileName, null]);
        } else if (activeModel.buildType === 'from_redshift_tables') {
          const { trainingTableName, validationTableName } =
            redshiftPage.variables;
          if (!trainingTableName && !validationTableName) return null;
          facts.push(['Training table', trainingTableName, null]);
          facts.push(['Validation table', validationTableName, null]);
        } else {
          return null;
        }

        const training = overviews.get('training');
        const validation = overviews.get('validation');
        if (!training || !validation) return null;

        const count = (n: number | undefined) =>
          n === null || n === undefined ? '-' : numeral(n).format('0,0');

        const fact = (
          label: string,
          one: React.ReactNode,
          other: React.ReactNode
        ) => {
          if (!one && !other) return null;
          // A file or table name is an identifier, not a phrase, so it wraps differently.
          const isId = typeof one === 'string';
          return (
            <div
              className={`dsum-fact${isId ? ' dsum-fact--id' : ''}`}
              key={label}
            >
              <span className="dsum-fact-label">{label}</span>
              {/* When the two datasets disagree, BOTH sides are named. Labelling only
                  the second one left the first unlabelled and therefore ambiguous: a
                  reader had to know that the unmarked value was the training one. */}
              {other ? (
                <span className="dsum-fact-value dsum-fact-value--split">
                  <span className="dsum-fact-side">
                    <span className="dsum-fact-side-label">training</span>
                    {one}
                  </span>
                  <span className="dsum-fact-side">
                    <span className="dsum-fact-side-label">validation</span>
                    {other}
                  </span>
                </span>
              ) : (
                <span className="dsum-fact-value">{one}</span>
              )}
            </div>
          );
        };

        const figures = (
          name: string,
          kind: 'training' | 'validation',
          set: typeof training
        ) => (
          <div className={`dsum-set dsum-set--${kind}`}>
            <span className="dsum-set-name">{name}</span>
            <span className="dsum-big">{count(set.records)}</span>
            <span className="dsum-unit">records</span>
            <span className="dsum-sub">
              {count(set.conversions)} converted
              {' · '}
              {numeral(set.rate).format('0.0')}%{conversionRateHint}
            </span>
            {/* The judgement this card already made, in words. It was an icon with the
                sentence behind a hover, so the one thing on the card that is a WARNING
                was the only thing you could not read without pointing at it. */}
            {set.dataSetQuality !== 'good' && (
              <span
                className={`dsum-flag text-${
                  dataQualityVariants[set.dataSetQuality]
                }`}
              >
                <i aria-hidden className="fas fa-exclamation-circle" />
                {set.dataSetQuality === 'bad'
                  ? 'Dataset is too small'
                  : 'Medium size dataset'}
              </span>
            )}
          </div>
        );

        return (
          <div className="dsum">
            <div className="dsum-facts">
              {facts.map(([label, one, other]) => fact(label, one, other))}
            </div>
            {timeline && <TimelineBands timeline={timeline} />}
            <div className="dsum-counts">
              {figures('Training', 'training', training)}
              {figures('Validation', 'validation', validation)}
            </div>
          </div>
        );
      }

      render() {
        const { tenant, modelId, cleaningCache } = this.props;
        const { activeModel, isAllowedToEdit } = this.props.store;
        const { isLoading, currentLoadingModels } = this.state;

        if (isLoading) {
          return <Spinner />;
        }

        const cantLoad =
          currentLoadingModels.length >= MAX_NUMBER_OF_LOADING_DATASETS;

        const paramsPath =
          activeModel.type === 'customer_fit'
            ? `/tenant/${tenant}/models/${modelId}/import_data`
            : `/tenant/${tenant}/models/${modelId}/import_data/${activeModel.type}`;

        const rows = this.buildRows();
        const summary = this.renderSummary();

        return (
          <>
            <Card border="light" className="dsum-card">
              <Card.Body>
                <div className="d-flex align-items-start flex-wrap gap-2 mb-2">
                  <h5 className="fw-bold mb-0 me-auto">
                    Training and Validation Data
                  </h5>
                  {checkPermissions(
                    PROFILES_PERMISSIONS.ARCHITECT,
                    <ButtonToolbar className="gap-2">
                      {isAllowedToEdit && (
                        <OverlayTrigger
                          show={cantLoad}
                          overlay={
                            <Tooltip>
                              {`Model ${currentLoadingModels[0]} and ${currentLoadingModels[1]} are currently loading. Please wait until they both load to begin loading another model`}
                            </Tooltip>
                          }
                        >
                          <span>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              disabled={cleaningCache || cantLoad}
                              onClick={this.toggleShowRefreshDatasetModal}
                            >
                              {activeModel.type === 'customer_fit'
                                ? 'Reload computations'
                                : 'Reload aggregations'}
                            </Button>
                          </span>
                        </OverlayTrigger>
                      )}

                      {/* One way in, and it opens where the work is.
                          
                          It was two buttons in one: "View parameters" on a live model
                          and "Change datasets source" on an editable one, both of them
                          navigating away. The dialog is the same in both states, since
                          the form already refuses every edit on a live model, so the
                          label no longer has to carry that distinction.

                          Customer fit only, because the dialog hosts the mk_audience
                          form. Every other build type keeps the route it has. */}
                      {activeModel.type === 'customer_fit' &&
                      activeModel.buildType === 'mk_audience' ? (
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={this.toggleShowDatasetModal}
                        >
                          View datasets
                        </Button>
                      ) : (
                        <LinkContainer to={paramsPath}>
                          <Button size="sm" variant="outline-secondary">
                            {activeModel.live ? (
                              'View parameters'
                            ) : (
                              <>
                                {activeModel.type === 'customer_fit'
                                  ? 'Change datasets source'
                                  : 'Change datasets'}
                              </>
                            )}
                          </Button>
                        </LinkContainer>
                      )}
                    </ButtonToolbar>
                  )}
                </div>

                {/* Scrolls in place rather than spilling. Two wrapped filenames
                    plus two number columns set a min-content width this card
                    cannot always give it, and with nothing to scroll the table
                    simply rendered past its own card and was cut off by the
                    first ancestor that clips.

                    Behind the flag even though it is a fix in both paths and
                    changes nothing at any width where the table already fits.
                    The shell is what makes it matter: its `overflow: hidden`
                    turns the old spill into a hard clip. Off the flag this
                    renders the bare table exactly as today, spill included. */}
                {summary ?? (
                  <ResponsiveLayoutContext.Consumer>
                    {(responsiveLayout) => {
                      const table = (
                        <Table className="align-middle mb-0" size="sm">
                          <thead className="table-light">
                            <tr>
                              <th className="fw-normal text-muted small" />
                              <th className="text-end">Training</th>
                              <th className="text-end">Validation</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((row) => (
                              <tr key={row.label}>
                                <th
                                  scope="row"
                                  className="fw-normal text-muted"
                                >
                                  {row.label}
                                  {row.hint}
                                </th>
                                <td className="text-end">{row.training}</td>
                                <td className="text-end">{row.validation}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      );
                      return responsiveLayout ? (
                        <div className="table-responsive">{table}</div>
                      ) : (
                        table
                      );
                    }}
                  </ResponsiveLayoutContext.Consumer>
                )}
              </Card.Body>
            </Card>
            <DatasetModal
              show={this.state.showDatasetModal}
              onHide={this.toggleShowDatasetModal}
              hasDatasets
            />
            <Modal
              show={this.state.showRefreshDatasetModal}
              onHide={this.toggleShowRefreshDatasetModal}
            >
              <Modal.Header>
                <Modal.Title>
                  {activeModel.type === 'customer_fit'
                    ? 'Reload computations?'
                    : 'Reload aggregations?'}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {activeModel.type === 'customer_fit' ? (
                  <>
                    <p>
                      This will start enriching again the training and
                      validation datasets to include any new computations or any
                      update made to existing computations.
                    </p>
                    <p>
                      Updated enrichment can potentially change this model if it
                      includes computations that are not static.{' '}
                      <a
                        href="https://help.madkudu.com/docs/reloading-your-computations"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-none text-primary"
                      >
                        Learn more.
                      </a>
                    </p>
                  </>
                ) : (
                  <p>
                    This will start recomputing any new or updated aggregations
                    and aggregated events for the records in the training and
                    validation datasets to allow you to use them in this model.
                  </p>
                )}

                <p className="fw-bold">
                  It may take a few minutes to several hours where the model
                  will not be accessible for this duration. You&apos;ll receive
                  a confirmation email when finished.
                </p>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="outline-primary"
                  onClick={this.toggleShowRefreshDatasetModal}
                >
                  Cancel
                </Button>
                <Button onClick={this.reloadDataset}>Start reloading</Button>
              </Modal.Footer>
            </Modal>
          </>
        );
      }
    }
  )
);
